import { io, Socket } from 'socket.io-client';
import { socketUrl } from '../services/socket';

export interface PlayerInput {
  x: number;
  face: string;
  isShooting: boolean;
  /**
   * Optional direction input. Snake (and any future movement-driven game)
   * sets this to 'up' | 'down' | 'left' | 'right' instead of the legacy
   * 'face' string used by UwU Invaders. The server-side socket.io handler
   * ignores unknown extra fields, so consumers that don't need it can
   * leave it undefined.
   */
  direction?: 'up' | 'down' | 'left' | 'right';
  /**
   * Optional chess-move payload. CyberChessActivity sends a structured
   * `{from, to, promotion?}` here when the local player clicks a
   * square. server.ts forwards the payload as `{id, type:'chess-move',
   * move}` via the 'player-update' socket channel that peers receive.
   * Kept loose vs the chess.js Move object so callers don't need the
   * full move-history struct for a simple LAN sender.
   */
  chessMove?: { from: string; to: string; promotion?: string };
}

export interface PlayerState {
  id: string;
  name: string;
  x: number;
  y: number;
  face: string;
  color: string;
  isHost: boolean;
  isShooting?: boolean;
  /**
   * Optional direction carried alongside the legacy UwU `face` string.
   * Snake and any future movement-driven game set this to
   * 'up' | 'down' | 'left' | 'right' so the host can fold the peer's
   * pending turn into the next state-sync. Mirrors the optional
   * `direction?` slot on PlayerInput.
   */
  direction?: 'up' | 'down' | 'left' | 'right';
}

export interface Alien {
  id: string;
  x: number;
  y: number;
  type: string;
}

export interface Bullet {
  id: string;
  x: number;
  y: number;
  ownerId: string;
}

export interface GameStateSync {
  aliens: Alien[];
  bullets: Bullet[];
  score: number;
  lives: number;
  level: number;
  wave: number;
  state: string;
  /**
   * Snake multiplayer state, host-authoritative. The UwU Invaders
   * client (and any other game that doesn't need it) ignores this slot.
   * See `src/components/games/SnakeActivity.tsx` for the concrete shape.
   *
   * Kept loose (`unknown`) at the network-manager boundary so adding a new
   * multiplayer game doesn't require bumping this interface every time.
   */
  snakeState?: unknown;
  /**
   * CyberChess multiplayer state, host-authoritative. SnakeActivity and
   * UwU Invaders clients ignore this slot. See
   * `src/components/games/CyberChessActivity.tsx` for the concrete shape
   * (fen, turn, clocks, lastMove, gameOver).
   *
   * Same `unknown` boundary treatment as snakeState — keeps the network
   * foundation stable across new multiplayer game registrations.
   */
  chessState?: unknown;
}

export class NetworkManager {
  socket: Socket;
  vaultId: string;
  gameType: string;
  isHost: boolean = false;
  /**
   * Local player's color slot. CyberChessActivity uses this to derive
   * board orientation and whose turn it is. Default 'spectator' so
   * NetworkManager can be constructed before the server emits the
   * role-assignment event (during the connecting phase the consumer
   * disables UI; once the event lands it's a plain setState).
   */
  myColor: 'w' | 'b' | 'spectator' = 'spectator';
  /**
   * Legacy host-assignment callback. Preserved exactly for UwU Invaders
   * + Snake — both pass `onHostAssignment?: (isHost: boolean) => void`
   * and treat the boolean as a role bit (truthy ⇒ host, falsy ⇒ peer).
   * CyberChessActivity ALSO listens to this — when isHost is true it
   * broadcasts state-sync; when false it sends player-input.
   */
  onHostAssignment?: (isHost: boolean) => void;
  /**
   * CyberChess-specific role callback. Fires on the dedicated
   * 'role-assignment' socket event the server emits ON TOP of the
   * legacy boolean so chess always has an unambiguous color. The
   * `reason` is the slot-transition that fired (first-joiner /
   * black-slot-open / two-player-full / non-chess-game / opponent-left
   * / etc.) so the consumer can show a graceful "you've been promoted
   * back to spectator if black left" toast.
   */
  onRoleAssignment?: (
    color: 'w' | 'b' | 'spectator',
    reason: string
  ) => void;
  onPlayerUpdate?: (data: PlayerState) => void;
  onStateSync?: (state: GameStateSync) => void;
  onGameChanged?: (gameType: string) => void;
  /**
   * Fires on the HOST only, when a peer clicks "Request to Play" on a
   * multiplayer tile in the arcade. Only the host can `change-game`, so
   * this is how a non-host expresses interest instead of staring at an
   * inert "waiting for host" label.
   */
  onGameRequested?: (data: { gameType: string; requesterId?: string }) => void;
  onPlayerList?: (players: string[]) => void;

  constructor(vaultId: string, gameType: string = 'uwu') {
    this.vaultId = vaultId;
    this.gameType = gameType;
    
    this.socket = io(socketUrl, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // Bound how hard the client keeps hitting an unreachable signaling
      // server. Each retry is a wasted round-trip to the backend, and when
      // it's down (404/CORS-blocked, e.g. the render.com instance sleeping)
      // the unbounded default made the app feel sluggish while a storm of
      // failed polls piled up in the console.
      reconnectionDelayMax: 10000,
      timeout: 6000,
    });

    this.socket.on('connect', () => {
      if (import.meta.env.DEV) console.log('Connected to server');
      this.socket.emit('join-vault', { vaultId: this.vaultId, gameType: this.gameType });
    });

    this.socket.on('connect_error', (err) => {
      if (import.meta.env.DEV) console.error('Connection error:', err);
    });

    this.socket.on('reconnect_failed', () => {
      if (import.meta.env.DEV) {
        console.warn('[NetworkManager] Game server unreachable; stopped reconnecting.');
      }
    });

    this.socket.on('host-assignment', (isHost: boolean) => {
      this.isHost = isHost;
      this.onHostAssignment?.(isHost);
    });

    /**
     * CyberChess role-assignment event. The server emits this in
     * ADDITION to (not instead of) the legacy boolean so games that
     * don't care about color (UwU Invaders, Snake, …) keep working
     * untouched. CyberChessActivity uses the dedicated slot to
     * orient its board and gate whose turn it is. UwU / Snake
     * listeners ignore this event entirely.
     */
    this.socket.on('role-assignment', (data: { color: 'w' | 'b' | 'spectator'; reason: string }) => {
      this.myColor = data.color;
      this.onRoleAssignment?.(data.color, data.reason);
    });

    this.socket.on('player-update', (data: PlayerState) => {
      if (data.id !== this.socket.id) {
        this.onPlayerUpdate?.(data);
      }
    });

    this.socket.on('state-sync', (state: GameStateSync) => {
      if (!this.isHost) {
        this.onStateSync?.(state);
      }
    });

    this.socket.on('game-changed', (gameType: string) => {
      this.gameType = gameType;
      this.onGameChanged?.(gameType);
    });

    this.socket.on('game-requested', (data: { gameType: string; requesterId?: string }) => {
      this.onGameRequested?.(data);
    });

    this.socket.on('player-list', (players: string[]) => {
      this.onPlayerList?.(players);
    });
  }

  changeGame(gameType: string) {
    if (this.isHost) {
      this.socket.emit('change-game', { vaultId: this.vaultId, gameType });
    }
  }

  /**
   * Non-host counterpart to changeGame — asks the host to start `gameType`.
   * The server relays it to the host socket only.
   */
  requestGame(gameType: string) {
    this.socket.emit('request-game', { vaultId: this.vaultId, gameType });
  }

  sendInput(input: PlayerInput) {
    this.socket.emit('player-input', { vaultId: this.vaultId, input });
  }

  /**
   * CyberChess helper. Wraps `sendInput` with the chess-move payload
   * shape so callers don't have to assemble the wrapping `face`/`x`/
   * `isShooting` boilerplate themselves. Sent over the same
   * 'player-input' socket channel as Snake's direction inputs and
   * UwU Invaders' actions — server-side handler emits it as
   * `{id, type:'chess-move', move}` to peers via 'player-update'.
   *
   * Only valid on CyberChessActivity paths; if non-chess consumers
   * accidentally call it, the server forwards `chessMove` on
   * player-update and peers who don't read it just ignore.
   */
  sendChessMove(chessMove: { from: string; to: string; promotion?: string }) {
    this.sendInput({
      x: 0,
      face: 'chess',
      isShooting: false,
      chessMove,
    });
  }

  sendState(state: GameStateSync) {
    if (this.isHost) {
      this.socket.emit('game-state', { vaultId: this.vaultId, state });
    }
  }

  disconnect() {
    this.socket.disconnect();
  }
}
